// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { headerStyles } from "@rpsc/Overrides/Pages/Header";
import { DivWrapper } from "@rpsc/XDL/constants";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, companyLogoItem, layoutContainer, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

headerStyles.page = {
    fullWidth: [true, true, true, true, true],
    padding: 0,
    css: css`
        box-shadow: 0 1px 0 0 ${getColor("common.border")};
        z-index: ${getProp("theme.zIndex.header")};
        position: relative;
    `,
};

headerStyles.container = {
    css: css`
        background-color: ${getColor("common.background")};
        display: grid;
        gap: 48px;
        .wrapper,
        .wrapper img {
            // Make logo take full width
            max-width: 100%;
            ${({ isCheckoutPage }: any) =>
                isCheckoutPage &&
                css`
                    &,
                    a,
                    span {
                        max-height: 63px;
                    }
                    min-height: 63px;
                `}
        }
        padding: 12px 0;
        grid-template-columns: auto minmax(0, 1fr);
        position: relative;
        ${({ isCheckoutPage }: any) => (isCheckoutPage ? "padding: 12px 0; align-items: center;" : "align-items: end;")}
        ${layoutContainer}

        ${xScreen("lte", "md")} {
            ${({ isCheckoutPage }: any) =>
                !isCheckoutPage
                    ? "grid-template-columns: 30px auto minmax(0, 1fr); padding: 8px 0;"
                    : "grid-template-columns: auto 1fr;"}
            ${({ isSticky }: any) =>
                isSticky &&
                css`
                    width: 100%;
                    box-shadow: 0 1px 0 0 ${color("common.border")};
                    position: fixed;
                    left: 0;
                    animation: slideDown 0.5s ease forwards;
                `}
            padding: 8px 0;
            align-items: center;
            gap: 16px;

            .wrapper,
            .wrapper img {
                // Make logo take full width
                max-width: 100%;
                max-height: 40px;
                ${xScreen("lte", "md")} {
                    max-height: 100%;
                }
            }
            .wrapper {
                margin-right: 32px;
            }

            @keyframes slideDown {
                from {
                    top: -105px;
                }
                to {
                    top: 0;
                }
            }
        }
    `,
};

headerStyles.headerWrapper = {
    css: css`
        &&&& {
            ${xScreen("lte", "md")} {
                z-index: ${getProp("theme.zIndex.header")};
            }

            @media print {
                display: none;
            }
        }
    `,
};

headerStyles.printLogo = companyLogoItem;

headerStyles.MainHeaderContainer = {
    css: css`
        ${({ isCheckoutPage }: any) => (isCheckoutPage ? "display: none;" : "display: flex;")}
        flex-direction: column;
        gap: 32px;
        position: relative;
        ${xScreen("lte", 767)} {
            flex-direction: row;
            justify-content: end;
            position: static; // fallback to parent;
        }
    `,
};

headerStyles.upperLineHeader = {
    css: css`
        display: grid;
        gap: 48px;
        align-items: center;
        grid-template-columns: 1fr auto;
        ${xScreen("lte", "lg")} {
            grid-template-columns: minmax(0, 721px) minmax(277px, 1fr);
        }
        justify-content: space-between;
        ${DivWrapper} > div {
            flex-wrap: nowrap;
        }

        ${xScreen("lte", "md")} {
            grid-template-columns: 1fr auto;
            gap: 59px;
            ${DivWrapper} > div {
                gap: 16px;
            }
        }
        ${xScreen("lte", "sm")} {
            grid-template-columns: auto auto;
            gap: 16px;
        }
    `,
};

headerStyles.bottomLineHeader = {
    css: css`
        display: flex;
        align-items: center;
    `,
};

headerStyles.checkoutNeedHelp = {
    css: css`
        margin-left: auto;
        ${TypographyStyle}, a {
            font-size: 14px;
            line-height: 19px;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            font-weight: ${fontConfig.weight.bold};
        }
        ${({ CMSMode }: any) => CMSMode && "position: absolute; bottom: -100px;"}
    `,
};

headerStyles.placeholder = {
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            visibility: hidden;
            height: ${({ height }: any) => height}px;
        }
    `,
};
